import { useState } from 'react';
import { useCreateGrant, useGrantableRoles } from '../../../hooks/useIam';
import { useToast } from '../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../utils/apiError';
import type { IamScopeType } from '../../../services/iam';
import { PracticeScopeSearch } from './PracticeScopeSearch';
import { RolePicker } from './RolePicker';

interface AddGrantFormProps {
    userId: string;
    onDone: () => void;
    onCancel: () => void;
}

const SCOPE_TYPE_LABEL: Record<IamScopeType, string> = {
    platform: 'Platform',
    practice: 'Practice',
    patient: 'Patient'
};
const SCOPE_TYPES = Object.keys(SCOPE_TYPE_LABEL) as IamScopeType[];

const INPUT_CLASS =
    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm text-gray-700 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500';

function toIsoEndOfDay(date: string): string {
    return new Date(`${date}T23:59:59`).toISOString();
}

// Inline compose form, expanded at the top of the Grants list instead of a
// modal: role (searchable), scope (platform/practice/patient), optional expiry.
export function AddGrantForm({ userId, onDone, onCancel }: AddGrantFormProps) {
    const { showToast } = useToast();
    const { roles, isLoading: rolesLoading } = useGrantableRoles();
    const { mutateAsync: create, isPending } = useCreateGrant();

    const [roleKey, setRoleKey] = useState('');
    const [scopeType, setScopeType] = useState<IamScopeType | ''>('');
    const [scopeId, setScopeId] = useState('');
    const [practiceName, setPracticeName] = useState<string | null>(null);
    const [expiresAt, setExpiresAt] = useState('');

    const selectedRole = roles.find((role) => role.key === roleKey);

    const handleScopeTypeChange = (type: IamScopeType) => {
        setScopeType(type);
        setScopeId('');
        setPracticeName(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!roleKey) {
            showToast('Select a role', 'error');
            return;
        }
        if (!scopeType) {
            showToast('Select a scope', 'error');
            return;
        }
        if (scopeType !== 'platform' && !scopeId.trim()) {
            showToast(`Select a ${scopeType} to scope this grant to`, 'error');
            return;
        }

        try {
            await create({
                userId,
                roleKey,
                scopeType,
                scopeId: scopeType === 'platform' ? undefined : scopeId.trim(),
                expiresAt: expiresAt ? toIsoEndOfDay(expiresAt) : undefined
            });
            showToast('Grant added', 'success');
            onDone();
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to add grant'), 'error');
        }
    };

    return (
        <form
            onSubmit={(e) => {
                void handleSubmit(e);
            }}
            className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4"
        >
            <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Role *</label>
                <RolePicker
                    roles={roles}
                    isLoading={rolesLoading}
                    value={roleKey}
                    onChange={setRoleKey}
                />
                {selectedRole && !selectedRole.grantable && (
                    <p className="mt-1.5 text-[11px] text-gray-400">
                        Grants nothing yet: no route enforces its permissions.
                    </p>
                )}
            </div>

            <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Scope *</label>
                <div className="grid grid-cols-3 gap-2">
                    {SCOPE_TYPES.map((type) => (
                        <button
                            key={type}
                            type="button"
                            onClick={() => handleScopeTypeChange(type)}
                            className={`px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                                scopeType === type
                                    ? 'border-gray-800 bg-gray-800 text-white'
                                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            {SCOPE_TYPE_LABEL[type]}
                        </button>
                    ))}
                </div>
            </div>

            {scopeType === 'practice' && (
                <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Practice *
                    </label>
                    <PracticeScopeSearch
                        selectedId={scopeId}
                        selectedLabel={practiceName}
                        onSelect={(id, name) => {
                            setScopeId(id);
                            setPracticeName(name);
                        }}
                        onClear={() => {
                            setScopeId('');
                            setPracticeName(null);
                        }}
                    />
                </div>
            )}

            {scopeType === 'patient' && (
                <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Patient id *
                    </label>
                    <input
                        type="text"
                        value={scopeId}
                        onChange={(e) => setScopeId(e.target.value)}
                        placeholder="e.g. the patient's id"
                        className={`${INPUT_CLASS} font-mono`}
                    />
                </div>
            )}

            <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Expires</label>
                <input
                    type="date"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    className={INPUT_CLASS}
                />
                <p className="mt-1.5 text-[11px] text-gray-400">
                    Leave empty for a grant that never expires on its own.
                </p>
            </div>

            <div className="flex justify-end items-center gap-3 pt-1">
                <button
                    type="button"
                    onClick={onCancel}
                    className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={isPending}
                    className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isPending ? 'Adding…' : 'Add grant'}
                </button>
            </div>
        </form>
    );
}
